import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, FileText, GraduationCap, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, getToken } from "@/api";
import { getChapters, getSubjects } from "@/data/curriculum";
import AcademicContextBar from "@/components/AcademicContextBar";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import Paywall from "@/components/Paywall";

/* Exam Mode — the ONLY place with marks budgets and a timer.
   Normal practice stays 5 questions; exams are longer papers. */

const MODES = [
  {
    key: "chapter",
    icon: BookOpen,
    title: "Chapter Test",
    desc: "8 questions from one chapter",
    meta: "8 questions · 10 marks · ~15 min",
    testId: "exam-mode-chapter",
  },
  {
    key: "unit",
    icon: FileText,
    title: "Unit Test",
    desc: "15 questions across 2–4 chapters",
    meta: "15 questions · 20 marks · ~30 min",
    testId: "exam-mode-unit",
  },
  {
    key: "board",
    icon: GraduationCap,
    title: "Exam Mode",
    desc: "Full-syllabus paper, board pattern",
    meta: "40 or 20 marks · timed",
    testId: "exam-mode-board",
  },
];

const pill = (active) =>
  `rounded-full border-2 px-4 py-2.5 text-xs font-bold transition-[border-color,background-color] duration-200 ${
    active
      ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
      : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/60 hover:border-[#16A34A]/40"
  }`;

const ExamSetup = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState(null);
  const [subject, setSubject] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [unitChapters, setUnitChapters] = useState([]);
  const [examMarks, setExamMarks] = useState(40);
  const [generating, setGenerating] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/login", { replace: true });
      return;
    }
    apiFetch("/me")
      .then((u) => setMe(u))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const boardKey = me?.board === "CBSE" ? "CBSE" : "CISCE";
  const klass = me?.klass ? String(me.klass) : null;
  // ALL subjects valid for the saved Board/Class — never profile-restricted
  const subjects = boardKey && klass ? getSubjects(boardKey, klass) : [];
  const chapterOptions = boardKey && klass && subject ? getChapters(boardKey, klass, subject) : [];

  // "Change Data" — refresh context, drop selections invalid for the new class
  const handleContextChange = (newBoard, newKlass) => {
    setMe((prev) => (prev ? { ...prev, board: newBoard, klass: String(newKlass) } : prev));
    setSubject(null);
    setChapter(null);
    setUnitChapters([]);
  };

  const ready =
    mode &&
    subject &&
    (mode === "chapter"
      ? Boolean(chapter)
      : mode === "unit"
        ? unitChapters.length >= 2
        : chapterOptions.length > 0);

  const toggleUnitChapter = (c) =>
    setUnitChapters((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : prev.length < 4 ? [...prev, c] : prev
    );

  const generate = async () => {
    setGenerating(true);
    try {
      const chapters =
        mode === "chapter" ? [chapter] : mode === "unit" ? unitChapters : chapterOptions;
      const data = await apiFetch("/exam-mode/generate", {
        method: "POST",
        body: {
          board: me?.board || "CBSE",
          klass: klass || "10",
          subject,
          mode,
          chapters,
          ...(mode === "board" ? { marks: examMarks } : {}),
        },
      });
      navigate("/exam", {
        state: {
          questions: data.questions,
          exam: data.exam,
          board: me?.board || "CBSE",
          klass: klass || "10",
        },
      });
    } catch (err) {
      if (err.message === "FREE_LIMIT_REACHED") setPaywallOpen(true);
      else
        toast.error(
          "Abhyas AI is busy right now — your generation was NOT used. Try again in a minute."
        );
      setGenerating(false);
    }
  };

  return (
    <div
      data-testid="exam-mode-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <button
          data-testid="exam-back-btn"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] transition-colors duration-200 hover:border-[#FACC15]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </button>
        <UserAvatar />
      </header>

      <main className="relative mx-auto max-w-2xl px-6 pb-20 pt-4">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/15 bg-[#16A34A]/8 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#14532D]">
          <Timer className="h-3.5 w-3.5" />
          Exam Mode
        </span>
        <h1 className="mt-4 font-display text-3xl font-extrabold sm:text-4xl">
          Timed papers, <span className="italic text-[#16A34A]">real exam feel</span>
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#647067] sm:text-base">
          Longer papers with real marks budgets and a countdown. Results
          feed straight into your Progress and weak areas.
        </p>

        {/* Mode selection */}
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                data-testid={m.testId}
                onClick={() => {
                  setMode(m.key);
                  setChapter(null);
                  setUnitChapters([]);
                }}
                className={`rounded-[1.5rem] border-2 bg-white p-5 text-left transition-[border-color,transform] duration-200 ${
                  active ? "border-[#16A34A]" : "border-[#E3ECE6] hover:border-[#16A34A]/40"
                }`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    active ? "bg-[#16A34A] text-white" : "bg-[#16A34A]/10 text-[#16A34A]"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <p className="mt-3 font-display text-base font-extrabold">{m.title}</p>
                <p className="mt-1 text-xs font-semibold leading-snug text-[#647067]">{m.desc}</p>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-[#94A39B]">
                  {m.meta}
                </p>
              </button>
            );
          })}
        </div>

        {mode && (
          <div
            data-testid="exam-config"
            className="animate-fade-up mt-6 rounded-[2rem] border border-[#E3ECE6] bg-white p-7 shadow-sm sm:p-8"
          >
            {me?.board && me?.klass && (
              <div className="mb-5">
                <AcademicContextBar
                  board={me.board}
                  klass={me.klass}
                  onChanged={handleContextChange}
                  testId="exam-context"
                />
              </div>
            )}
            <label className="mb-1.5 block text-sm font-bold">Subject</label>
            <div className="mb-5 flex flex-wrap gap-2">
              {subjects.map((s) => (
                <button
                  key={s}
                  data-testid={`exam-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => {
                    setSubject(s);
                    setChapter(null);
                    setUnitChapters([]);
                  }}
                  className={pill(subject === s)}
                >
                  {s}
                </button>
              ))}
            </div>

            {mode === "board" && (
              <>
                <label className="mb-1.5 block text-sm font-bold">Paper size</label>
                <div className="mb-5 flex flex-wrap gap-2">
                  <button
                    data-testid="exam-marks-40"
                    onClick={() => setExamMarks(40)}
                    className={pill(examMarks === 40)}
                  >
                    Full · 40 marks · 15 questions · ~60 min
                  </button>
                  <button
                    data-testid="exam-marks-20"
                    onClick={() => setExamMarks(20)}
                    className={pill(examMarks === 20)}
                  >
                    Half · 20 marks · 10 questions · ~30 min
                  </button>
                </div>
              </>
            )}

            {mode === "chapter" && (
              <>
                <label className="mb-1.5 block text-sm font-bold">Chapter</label>
                <select
                  data-testid="exam-chapter-select"
                  value={chapter || ""}
                  onChange={(e) => setChapter(e.target.value)}
                  disabled={chapterOptions.length === 0}
                  className="mb-5 w-full cursor-pointer appearance-none rounded-xl border-2 border-[#17201A]/10 bg-[#F8FAF7] px-4 py-2.5 text-sm font-semibold outline-none focus:border-[#16A34A]"
                >
                  <option value="">Select chapter</option>
                  {chapterOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </>
            )}

            {mode === "unit" && (
              <>
                <label className="mb-1.5 block text-sm font-bold">
                  Chapters{" "}
                  <span className="font-semibold text-[#94A39B]">
                    (pick 2–4 · {unitChapters.length} selected)
                  </span>
                </label>
                <div data-testid="exam-unit-chapters" className="mb-5 flex flex-wrap gap-2">
                  {chapterOptions.map((c) => (
                    <button
                      key={c}
                      data-testid={`exam-unit-chapter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                      onClick={() => toggleUnitChapter(c)}
                      className={pill(unitChapters.includes(c))}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </>
            )}

            {mode === "board" && (
              <p data-testid="exam-board-scope" className="mb-5 rounded-xl bg-[#F8FAF7] px-4 py-3 text-xs font-semibold text-[#647067]">
                Full {subject} syllabus — {chapterOptions.length} chapters, board
                examination pattern.
              </p>
            )}

            <button
              data-testid="exam-generate-btn"
              data-magnetic="true"
              onClick={generate}
              disabled={!ready || generating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,opacity] duration-200 hover:bg-[#15803D] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Setting your paper...
                </>
              ) : (
                <>
                  <Timer className="h-4 w-4" />
                  Generate Exam Paper
                </>
              )}
            </button>
            <p className="mt-3 text-center text-xs font-semibold text-[#647067]">
              Uses 1 practice generation from your plan
            </p>
          </div>
        )}
      </main>
      <Paywall open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
};

export default ExamSetup;
